import React, { useState, useEffect, useCallback } from 'react';
import Navbar from '../components/Navbar';
import { getAiApiBaseUrl, getApiHeaders } from '../config/api';

// Types
interface CampaignStats {
  Date: string;
  CampaignType: string;
  Platform: string;
  Sent: number;
  Delivered: number;
  Clicked: number;
  Received: number;
  Viewed: number;
  Dismissed: number;
  ClickRate: number;
  DeliveryRate: number;
  ViewRate: number;
  DismissRate: number;
  ResurrectedUsers: number;
  ResurrectionRate: number;
  SentToInactiveUsers: number;
  DirectClicks: number;
  IndirectReturns: number;
  TotalAttributed: number;
  AttributionRate: number;
}

interface PermissionStats {
  granted: number;
  denied: number;
  notDetermined: number;
  provisional: number;
  appDeleted: number;
}

interface RecentEvent {
  EventID: string;
  EventType: string;
  UserID: string;
  CampaignType: string;
  Platform: string;
  EventTimestamp: string;
  NotificationType: string;
}

// Campaign type colors for consistent styling
const campaignColors: Record<string, string> = {
  'STORY_PING': '#FF6B6B',
  'STORY_REMINDER': '#FF8E53',
  'STORY_NEW_REMINDER': '#FFB347',
  'STORY_EXPIRING': '#FFD93D',
  'WINBACK_INACTIVE': '#6BCB77',
  'WEEKLY_WINBACK': '#4D96FF',
  'PING_MISSED_RECAP': '#9B59B6',
  'ACTIVITY_FOLLOW': '#E91E63',
  'ACTIVITY_LIKE': '#FF4081',
  'ACTIVITY_COMMENT': '#FF5722',
  'CHAT_MESSAGE': '#2196F3',
  'UNREAD_DMS': '#00BCD4',
  'SUBSCRIPTION': '#9C27B0',
  'DEFAULT': '#7F8C8D',
};

const getCampaignColor = (type: string): string => {
  return campaignColors[type] || campaignColors['DEFAULT'];
};

// Format large numbers
const formatNumber = (num: number): string => {
  if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
  if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
  return num.toString();
};

// Format percentage
const formatPercent = (value: number): string => {
  return value.toFixed(1) + '%';
};

const NotificationAnalytics: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dateRange, setDateRange] = useState<'1d' | '7d' | '30d' | '90d'>('1d');
  const [selectedPlatform, setSelectedPlatform] = useState<'all' | 'ios' | 'android'>('all');
  const [campaignStats, setCampaignStats] = useState<CampaignStats[]>([]);
  const [permissionStats, setPermissionStats] = useState<PermissionStats | null>(null);
  const [recentEvents, setRecentEvents] = useState<RecentEvent[]>([]);
  const [refreshInterval, setRefreshInterval] = useState<number | null>(30000);

  // Calculate aggregated stats
  const aggregatedStats = React.useMemo(() => {
    const filtered = campaignStats.filter(
      s => selectedPlatform === 'all' || s.Platform === selectedPlatform
    );

    const totalSent = filtered.reduce((sum, s) => sum + s.Sent, 0);
    const totalClicked = filtered.reduce((sum, s) => sum + s.Clicked, 0);
    const totalDelivered = filtered.reduce((sum, s) => sum + s.Delivered, 0);
    const totalResurrected = filtered.reduce((sum, s) => sum + s.ResurrectedUsers, 0);
    const totalInactive = filtered.reduce((sum, s) => sum + s.SentToInactiveUsers, 0);

    return {
      totalSent,
      totalClicked,
      totalDelivered,
      totalResurrected,
      avgClickRate: totalDelivered > 0 ? (totalClicked / totalDelivered) * 100 : 0,
      avgDeliveryRate: totalSent > 0 ? (totalDelivered / totalSent) * 100 : 0,
      avgResurrectionRate: totalInactive > 0 ? (totalResurrected / totalInactive) * 100 : 0,
    };
  }, [campaignStats, selectedPlatform]);

  // Group by campaign type
  const campaignBreakdown = React.useMemo(() => {
    const filtered = campaignStats.filter(
      s => selectedPlatform === 'all' || s.Platform === selectedPlatform
    );

    const grouped: Record<string, { sent: number; clicked: number; clickRate: number; resurrected: number }> = {};

    filtered.forEach(s => {
      if (!grouped[s.CampaignType]) {
        grouped[s.CampaignType] = { sent: 0, clicked: 0, clickRate: 0, resurrected: 0 };
      }
      grouped[s.CampaignType].sent += s.Sent;
      grouped[s.CampaignType].clicked += s.Clicked;
      grouped[s.CampaignType].resurrected += s.ResurrectedUsers;
    });

    // Calculate click rates
    Object.keys(grouped).forEach(key => {
      const g = grouped[key];
      g.clickRate = g.sent > 0 ? (g.clicked / g.sent) * 100 : 0;
    });

    return Object.entries(grouped)
      .map(([type, data]) => ({ type, ...data }))
      .sort((a, b) => b.sent - a.sent);
  }, [campaignStats, selectedPlatform]);

  // Helper to make POST requests
  const postRequest = async (endpoint: string, body: Record<string, unknown>, token: string | null) => {
    const res = await fetch(endpoint, {
      method: 'POST',
      headers: getApiHeaders(token || undefined),
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      throw new Error(`HTTP error! status: ${res.status}`);
    }
    return res.json();
  };

  // Fetch data
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('auth_token');
      const baseUrl = getAiApiBaseUrl();
      const analyticsEndpoint = `${baseUrl}/analytics`;

      // Calculate date range
      const endDate = new Date();
      const startDate = new Date();
      if (dateRange === '1d') startDate.setDate(endDate.getDate() - 1);
      else if (dateRange === '7d') startDate.setDate(endDate.getDate() - 7);
      else if (dateRange === '30d') startDate.setDate(endDate.getDate() - 30);
      else startDate.setDate(endDate.getDate() - 90);

      const [statsRes, permissionRes, eventsRes, queueMetricsRes] = await Promise.all([
        // Campaign Stats (new tracking - may be empty)
        postRequest(analyticsEndpoint, {
          action: 'getCampaignStats',
          startDate: startDate.toISOString(),
          endDate: endDate.toISOString(),
          platform: selectedPlatform === 'all' ? undefined : selectedPlatform,
          limit: 500,
        }, token).catch((err) => {
          console.error('Campaign stats error:', err);
          return { stats: [], summary: [] };
        }),

        // Permission Stats (new tracking - may be empty)
        postRequest(analyticsEndpoint, {
          action: 'getPermissionStats',
          platform: selectedPlatform === 'all' ? undefined : selectedPlatform,
        }, token).catch((err) => {
          console.error('Permission stats error:', err);
          return null;
        }),

        // Recent Events (new tracking - may be empty)
        postRequest(analyticsEndpoint, {
          action: 'getRecentEvents',
          limit: 20,
        }, token).catch((err) => {
          console.error('Recent events error:', err);
          return { events: [] };
        }),

        // Legacy queue metrics (from notificationsqueues - has real data!)
        postRequest(analyticsEndpoint, {
          action: 'metrics',
          days: dateRange === '1d' ? 1 : dateRange === '7d' ? 7 : dateRange === '30d' ? 30 : 90,
          // Don't filter by env - get all data
        }, token).catch((err) => {
          console.error('Queue metrics error:', err);
          return null;
        }),
      ]);

      // Parse campaign stats from new format
      const statsData = statsRes?.stats || [];
      let formattedStats = statsData.map((s: Record<string, unknown>) => ({
        Date: s.date,
        CampaignType: s.campaignType,
        Platform: s.platform,
        Sent: s.sent || 0,
        Delivered: s.delivered || 0,
        Clicked: s.clicked || 0,
        Received: s.received || 0,
        Viewed: s.viewed || 0,
        Dismissed: s.dismissed || 0,
        ClickRate: s.clickRate || 0,
        DeliveryRate: s.deliveryRate || 0,
        ViewRate: s.viewRate || 0,
        DismissRate: s.dismissRate || 0,
        ResurrectedUsers: s.resurrectedUsers || 0,
        ResurrectionRate: s.resurrectionRate || 0,
        SentToInactiveUsers: s.sentToInactiveUsers || 0,
        DirectClicks: s.directClicks || 0,
        IndirectReturns: s.indirectReturns || 0,
        TotalAttributed: s.totalAttributed || 0,
        AttributionRate: s.attributionRate || 0,
      }));

      // If new tracking data is empty, use legacy queue metrics
      if (formattedStats.length === 0 && queueMetricsRes?.byNotificationType) {
        formattedStats = queueMetricsRes.byNotificationType.map((m: Record<string, unknown>) => ({
          Date: new Date().toISOString(),
          CampaignType: m.notificationType || 'UNKNOWN',
          Platform: 'all',
          Sent: m.totalQueued || 0,
          Delivered: m.successfulSends || 0,
          Clicked: 0, // Not tracked in legacy system
          Received: 0,
          Viewed: 0,
          Dismissed: 0,
          ClickRate: 0, // Not tracked yet
          DeliveryRate: m.successRate || 0,
          ViewRate: 0,
          DismissRate: 0,
          ResurrectedUsers: 0,
          ResurrectionRate: 0,
          SentToInactiveUsers: 0,
          DirectClicks: 0,
          IndirectReturns: 0,
          TotalAttributed: 0,
          AttributionRate: 0,
        }));
      }

      // Parse permission stats from new format
      const formattedPermissions = permissionRes?.totalUsers > 0 ? {
        granted: permissionRes.permissionBreakdown?.GRANTED || 0,
        denied: permissionRes.permissionBreakdown?.DENIED || 0,
        notDetermined: permissionRes.permissionBreakdown?.NOT_DETERMINED || 0,
        provisional: permissionRes.permissionBreakdown?.PROVISIONAL || 0,
        appDeleted: permissionRes.failureStats?.likelyDeletedApp || 0,
      } : null;

      // Parse recent events from new format
      const eventsData = eventsRes?.events || [];
      const formattedEvents = eventsData.map((e: Record<string, unknown>) => ({
        EventID: e.eventId,
        EventType: e.eventType,
        UserID: e.userId,
        CampaignType: e.campaignType,
        Platform: e.platform,
        EventTimestamp: e.eventTimestamp,
        NotificationType: e.notificationType,
      }));

      setCampaignStats(formattedStats);
      setPermissionStats(formattedPermissions);
      setRecentEvents(formattedEvents);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch notification analytics:', err);
      setError('Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  }, [dateRange, selectedPlatform]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Auto-refresh
  useEffect(() => {
    if (!refreshInterval) return;
    const interval = setInterval(fetchData, refreshInterval);
    return () => clearInterval(interval);
  }, [refreshInterval, fetchData]);

  // PingTop brand gradient
  const brandGradient = 'from-[#FF9C15] to-[#F42026]';

  return (
    <div className='min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900'>
      <Navbar />

      <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8'>
        {/* Header */}
        <div className='flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8'>
          <div>
            <h1 className='text-3xl font-bold text-white mb-2'>
              <span className={`bg-gradient-to-r ${brandGradient} bg-clip-text text-transparent`}>
                Notification
              </span>{' '}
              Analytics
            </h1>
            <p className='text-gray-400'>Track campaign performance, click rates, and resurrection metrics</p>
          </div>

          {/* Filters */}
          <div className='flex flex-wrap gap-3'>
            {/* Date Range */}
            <select
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value as '1d' | '7d' | '30d' | '90d')}
              className='px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-orange-500'
            >
              <option value='1d' className='bg-slate-800'>Last 24 Hours</option>
              <option value='7d' className='bg-slate-800'>Last 7 Days</option>
              <option value='30d' className='bg-slate-800'>Last 30 Days</option>
              <option value='90d' className='bg-slate-800'>Last 90 Days</option>
            </select>

            {/* Platform */}
            <select
              value={selectedPlatform}
              onChange={(e) => setSelectedPlatform(e.target.value as 'all' | 'ios' | 'android')}
              className='px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-orange-500'
            >
              <option value='all' className='bg-slate-800'>All Platforms</option>
              <option value='ios' className='bg-slate-800'>iOS</option>
              <option value='android' className='bg-slate-800'>Android</option>
            </select>

            {/* Auto-refresh toggle */}
            <button
              onClick={() => setRefreshInterval(refreshInterval ? null : 30000)}
              className={`px-4 py-2 rounded-lg transition-all ${
                refreshInterval
                  ? 'bg-green-500/20 text-green-400 border border-green-500/30'
                  : 'bg-white/10 text-gray-400 border border-white/20'
              }`}
            >
              {refreshInterval ? '🔄 Auto-refresh ON' : '⏸ Auto-refresh OFF'}
            </button>

            {/* Manual refresh */}
            <button
              onClick={fetchData}
              disabled={loading}
              className={`px-4 py-2 bg-gradient-to-r ${brandGradient} text-white rounded-lg font-semibold transition-all hover:shadow-lg ${
                loading ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              {loading ? 'Loading...' : 'Refresh'}
            </button>
          </div>
        </div>

        {error && (
          <div className='mb-6 p-4 bg-red-500/20 border border-red-500/30 rounded-lg text-red-400'>
            {error}
          </div>
        )}

        {/* Key Metrics Cards */}
        <div className='grid grid-cols-2 md:grid-cols-4 gap-4 mb-8'>
          <div className='bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6'>
            <div className='text-3xl mb-2'>📤</div>
            <div className='text-2xl font-bold text-white'>{formatNumber(aggregatedStats.totalSent)}</div>
            <div className='text-sm text-gray-400'>Total Sent</div>
          </div>

          <div className='bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6'>
            <div className='text-3xl mb-2'>👆</div>
            <div className='text-2xl font-bold text-white'>{formatNumber(aggregatedStats.totalClicked)}</div>
            <div className='text-sm text-gray-400'>Total Clicks</div>
            <div className={`text-sm mt-1 ${aggregatedStats.avgClickRate >= 5 ? 'text-green-400' : 'text-yellow-400'}`}>
              {formatPercent(aggregatedStats.avgClickRate)} CTR
            </div>
          </div>

          <div className='bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6'>
            <div className='text-3xl mb-2'>✅</div>
            <div className='text-2xl font-bold text-white'>{formatPercent(aggregatedStats.avgDeliveryRate)}</div>
            <div className='text-sm text-gray-400'>Delivery Rate</div>
          </div>

          <div className='bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6'>
            <div className='text-3xl mb-2'>🔄</div>
            <div className='text-2xl font-bold text-white'>{formatNumber(aggregatedStats.totalResurrected)}</div>
            <div className='text-sm text-gray-400'>Resurrected Users</div>
            <div className={`text-sm mt-1 ${aggregatedStats.avgResurrectionRate >= 10 ? 'text-green-400' : 'text-yellow-400'}`}>
              {formatPercent(aggregatedStats.avgResurrectionRate)} Rate
            </div>
          </div>
        </div>

        {/* Campaign Performance Table */}
        <div className='bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6 mb-8'>
          <h2 className='text-xl font-bold text-white mb-4'>Campaign Performance</h2>

          <div className='overflow-x-auto'>
            <table className='w-full text-left'>
              <thead>
                <tr className='border-b border-white/10'>
                  <th className='pb-3 text-gray-400 font-medium'>Campaign Type</th>
                  <th className='pb-3 text-gray-400 font-medium text-right'>Sent</th>
                  <th className='pb-3 text-gray-400 font-medium text-right'>Clicked</th>
                  <th className='pb-3 text-gray-400 font-medium text-right'>CTR</th>
                  <th className='pb-3 text-gray-400 font-medium text-right'>Resurrected</th>
                  <th className='pb-3 text-gray-400 font-medium'>Performance</th>
                </tr>
              </thead>
              <tbody>
                {campaignBreakdown.map((campaign) => (
                  <tr key={campaign.type} className='border-b border-white/5 hover:bg-white/5 transition-colors'>
                    <td className='py-4'>
                      <div className='flex items-center gap-3'>
                        <div
                          className='w-3 h-3 rounded-full'
                          style={{ backgroundColor: getCampaignColor(campaign.type) }}
                        />
                        <span className='text-white font-medium'>{campaign.type.replace(/_/g, ' ')}</span>
                      </div>
                    </td>
                    <td className='py-4 text-right text-gray-300'>{formatNumber(campaign.sent)}</td>
                    <td className='py-4 text-right text-gray-300'>{formatNumber(campaign.clicked)}</td>
                    <td className='py-4 text-right'>
                      <span className={`font-medium ${
                        campaign.clickRate >= 5 ? 'text-green-400' :
                        campaign.clickRate >= 2 ? 'text-yellow-400' : 'text-red-400'
                      }`}>
                        {formatPercent(campaign.clickRate)}
                      </span>
                    </td>
                    <td className='py-4 text-right text-gray-300'>{formatNumber(campaign.resurrected)}</td>
                    <td className='py-4'>
                      <div className='w-32 h-2 bg-white/10 rounded-full overflow-hidden'>
                        <div
                          className='h-full rounded-full transition-all'
                          style={{
                            width: `${Math.min(campaign.clickRate * 10, 100)}%`,
                            backgroundColor: getCampaignColor(campaign.type),
                          }}
                        />
                      </div>
                    </td>
                  </tr>
                ))}
                {campaignBreakdown.length === 0 && (
                  <tr>
                    <td colSpan={6} className='py-8 text-center text-gray-400'>
                      No campaign data available for the selected period
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Two Column Layout */}
        <div className='grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8'>
          {/* Permission Stats */}
          <div className='bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6'>
            <h2 className='text-xl font-bold text-white mb-4'>Permission Status</h2>

            {permissionStats ? (
              <div className='space-y-4'>
                <div className='flex items-center justify-between'>
                  <div className='flex items-center gap-2'>
                    <div className='w-3 h-3 rounded-full bg-green-500' />
                    <span className='text-gray-300'>Granted</span>
                  </div>
                  <span className='text-white font-bold'>{formatNumber(permissionStats.granted)}</span>
                </div>

                <div className='flex items-center justify-between'>
                  <div className='flex items-center gap-2'>
                    <div className='w-3 h-3 rounded-full bg-red-500' />
                    <span className='text-gray-300'>Denied</span>
                  </div>
                  <span className='text-white font-bold'>{formatNumber(permissionStats.denied)}</span>
                </div>

                <div className='flex items-center justify-between'>
                  <div className='flex items-center gap-2'>
                    <div className='w-3 h-3 rounded-full bg-gray-500' />
                    <span className='text-gray-300'>Not Determined</span>
                  </div>
                  <span className='text-white font-bold'>{formatNumber(permissionStats.notDetermined)}</span>
                </div>

                <div className='flex items-center justify-between'>
                  <div className='flex items-center gap-2'>
                    <div className='w-3 h-3 rounded-full bg-yellow-500' />
                    <span className='text-gray-300'>Provisional</span>
                  </div>
                  <span className='text-white font-bold'>{formatNumber(permissionStats.provisional)}</span>
                </div>

                <div className='flex items-center justify-between'>
                  <div className='flex items-center gap-2'>
                    <div className='w-3 h-3 rounded-full bg-purple-500' />
                    <span className='text-gray-300'>App Deleted</span>
                  </div>
                  <span className='text-white font-bold'>{formatNumber(permissionStats.appDeleted)}</span>
                </div>

                {/* Visual bar */}
                <div className='mt-4 h-4 rounded-full overflow-hidden flex'>
                  <div
                    className='bg-green-500'
                    style={{
                      width: `${(permissionStats.granted / (permissionStats.granted + permissionStats.denied + permissionStats.notDetermined)) * 100}%`,
                    }}
                  />
                  <div
                    className='bg-red-500'
                    style={{
                      width: `${(permissionStats.denied / (permissionStats.granted + permissionStats.denied + permissionStats.notDetermined)) * 100}%`,
                    }}
                  />
                  <div className='bg-gray-500 flex-1' />
                </div>
              </div>
            ) : (
              <div className='text-center text-gray-400 py-8'>
                Permission data not available
              </div>
            )}
          </div>

          {/* Recent Events */}
          <div className='bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6'>
            <h2 className='text-xl font-bold text-white mb-4'>Recent Events</h2>

            <div className='space-y-3 max-h-80 overflow-y-auto'>
              {recentEvents.map((event) => (
                <div
                  key={event.EventID}
                  className='flex items-center justify-between p-3 bg-white/5 rounded-lg'
                >
                  <div className='flex items-center gap-3'>
                    <span className='text-2xl'>
                      {event.EventType === 'CLICK' && '👆'}
                      {event.EventType === 'RECEIVED' && '📥'}
                      {event.EventType === 'VIEWED' && '👁'}
                      {event.EventType === 'DISMISSED' && '❌'}
                    </span>
                    <div>
                      <div className='text-white font-medium'>{event.EventType}</div>
                      <div className='text-xs text-gray-400'>
                        {event.CampaignType?.replace(/_/g, ' ') || 'Unknown'}
                      </div>
                    </div>
                  </div>
                  <div className='text-right'>
                    <div className={`text-xs px-2 py-1 rounded ${
                      event.Platform === 'ios' ? 'bg-blue-500/20 text-blue-400' : 'bg-green-500/20 text-green-400'
                    }`}>
                      {event.Platform?.toUpperCase()}
                    </div>
                    <div className='text-xs text-gray-500 mt-1'>
                      {new Date(event.EventTimestamp).toLocaleTimeString()}
                    </div>
                  </div>
                </div>
              ))}
              {recentEvents.length === 0 && (
                <div className='text-center text-gray-400 py-8'>
                  No recent events
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Campaign Type Legend */}
        <div className='bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6'>
          <h2 className='text-xl font-bold text-white mb-4'>Campaign Types</h2>

          <div className='grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4'>
            {Object.entries(campaignColors).filter(([k]) => k !== 'DEFAULT').map(([type, color]) => (
              <div key={type} className='flex items-center gap-2'>
                <div
                  className='w-3 h-3 rounded-full'
                  style={{ backgroundColor: color }}
                />
                <span className='text-xs text-gray-400'>{type.replace(/_/g, ' ')}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotificationAnalytics;
